import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import {
  PublicDedicatedCalendar,
  AggregatedSlotsResponse,
  BookingRequest,
  BookingRequestResponse,
} from '../models/public-calendar.models';
import { PUBLIC_API_BASE_URL } from '../tokens';
import { PublicI18nService } from './public-i18n.service';

/**
 * Service for public calendar API calls.
 * These endpoints do NOT require authentication.
 */
@Injectable({
  providedIn: 'root',
})
export class PublicCalendarApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(PUBLIC_API_BASE_URL);
  private readonly i18n = inject(PublicI18nService);

  /**
   * Get dedicated calendar info by subdomain.
   * Returns provider info, theme settings, and available calendars.
   */
  getDedicatedCalendar(subdomain: string): Observable<PublicDedicatedCalendar> {
    return this.http
      .get<PublicDedicatedCalendar>(
        `${this.baseUrl}/api/public/calendar/subdomain/${encodeURIComponent(subdomain)}`
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Get aggregated available slots across all calendars for a provider.
   * A slot is available if ANY calendar is free at that time.
   */
  getAggregatedSlots(
    providerId: string,
    from: string,
    to: string
  ): Observable<AggregatedSlotsResponse> {
    return this.http
      .get<AggregatedSlotsResponse>(
        `${this.baseUrl}/api/public/calendar/providers/${providerId}/slots`,
        { params: { from, to } }
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Create a booking REQUEST (not a direct booking).
   * Customer submits their preferred time slot and contact info.
   * Provider will review and can send an offer.
   */
  createBookingRequest(
    providerId: string,
    request: BookingRequest
  ): Observable<BookingRequestResponse> {
    return this.http
      .post<BookingRequestResponse>(
        `${this.baseUrl}/api/public/calendar/providers/${providerId}/request`,
        request
      )
      .pipe(catchError((err) => this.handleError(err)));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = this.i18n.t('error.generic');

    if (error.status === 404) {
      errorMessage = this.i18n.t('error.notFound');
    } else if (error.status === 400) {
      errorMessage = error.error?.message || this.i18n.t('error.invalidRequest');
    } else if (error.status === 409) {
      errorMessage = this.i18n.t('error.slotUnavailable');
    } else if (error.status === 0) {
      errorMessage = this.i18n.t('error.noConnection');
    }

    return throwError(() => new Error(errorMessage));
  }
}
